import { useState, useRef, useEffect } from 'react';
import { storage, db } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useClub } from '../../contexts/ClubContext';

// Document types for player files
const DOCUMENT_TYPES = [
  { value: 'medical_form', label: 'Medical Form', required: true },
  { value: 'birth_certificate', label: 'Birth Certificate', required: true },
  { value: 'waiver', label: 'Liability Waiver', required: true },
  { value: 'id_photo', label: 'ID Photo', required: false },
  { value: 'passport', label: 'Passport', required: false },
  { value: 'other', label: 'Other', required: false }
];

function PlayerDocuments({ playerId, playerName }) {
  const { clubId } = useClub();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const fileInputRef = useRef(null);

  // Load documents for this player
  useEffect(() => {
    const loadDocuments = async () => {
      if (!playerId || !clubId) return;
      
      try {
        const q = query(
          collection(db, 'documents'),
          where('playerId', '==', playerId),
          where('clubId', '==', clubId)
        );
        const snapshot = await getDocs(q);
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docs);
      } catch (err) {
        console.error('Error loading documents:', err);
        setError('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    
    loadDocuments();
  }, [playerId, clubId]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedType) {
      setError('Please select a document type first');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Create unique filename
      const timestamp = Date.now();
      const filename = `${clubId}/${playerId}/${selectedType}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `documents/${filename}`);

      // Upload to Firebase Storage
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      const docData = {
        playerId,
        clubId,
        type: selectedType,
        typeName: DOCUMENT_TYPES.find(t => t.value === selectedType)?.label || selectedType,
        filename: file.name,
        storagePath: `documents/${filename}`,
        url: downloadURL,
        size: file.size,
        mimeType: file.type,
        uploadedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'documents'), docData);
      
      setDocuments([...documents, { id: docRef.id, ...docData }]);
      setSuccess('Document uploaded successfully!');
      setSelectedType('');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Delete ${document.typeName}: ${document.filename}?`)) return;

    try {
      // Delete from Storage
      const storageRef = ref(storage, document.storagePath);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, 'documents', document.id));

      setDocuments(documents.filter(d => d.id !== document.id));
      setSuccess('Document deleted');
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocumentIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.startsWith('image/')) return '🖼️';
    return '📁';
  };

  // Check which required documents are missing
  const missingRequired = DOCUMENT_TYPES
    .filter(t => t.required)
    .filter(t => !documents.some(d => d.type === t.value));

  return (
    <div className="player-documents">
      <h3>Player Documents</h3>
      
      {/* Missing Documents Warning */}
      {missingRequired.length > 0 && (
        <div className="missing-docs-warning">
          <span className="warning-icon">⚠️</span>
          <div>
            <strong>Missing Required Documents:</strong>
            <ul>
              {missingRequired.map(doc => (
                <li key={doc.value}>{doc.label}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="document-upload-section">
        <div className="upload-controls">
          <select 
            value={selectedType} 
            onChange={(e) => setSelectedType(e.target.value)}
            className="document-type-select"
          >
            <option value="">Select Document Type...</option>
            {DOCUMENT_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label} {type.required ? '*' : ''}
              </option>
            ))}
          </select>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={uploading || !selectedType}
            className="file-input"
          />
          {uploading && <span className="uploading-text">Uploading...</span>}
        </div>
        <p className="upload-hint">Accepted formats: PDF, JPG, PNG (max 5MB)</p>
      </div>

      {/* Messages */}
      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      {/* Documents List */}
      {loading ? (
        <p className="loading-text">Loading documents...</p>
      ) : documents.length === 0 ? (
        <div className="no-documents">
          <span className="empty-icon">📂</span>
          <p>No documents uploaded yet</p>
        </div>
      ) : (
        <div className="documents-list">
          {documents.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="document-icon">{getDocumentIcon(doc.mimeType)}</div>
              <div className="document-info">
                <span className="document-type">{doc.typeName}</span>
                <span className="document-name">{doc.filename}</span>
                <span className="document-size">{formatFileSize(doc.size)}</span>
              </div>
              <div className="document-actions">
                <a 
                  href={doc.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-view"
                >
                  View
                </a>
                <button 
                  onClick={() => handleDelete(doc)}
                  className="btn-delete-doc"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { DOCUMENT_TYPES };
export default PlayerDocuments;
